import type { PayrollImportRowStatus, PayrollImportStatus, PayslipStatus } from '$lib/api/types';

/** 원화 표기: 3,000,000원 */
export function formatWon(amount: number): string {
	return `${amount.toLocaleString('ko-KR')}원`;
}

export function payslipStatusLabel(status: PayslipStatus | string): string {
	const map: Record<string, string> = {
		DRAFT: '작성중',
		CONFIRMED: '확정',
		CANCELLED: '취소됨'
	};
	return map[status] ?? status;
}

export function payslipStatusVariant(
	status: PayslipStatus | string
): 'default' | 'secondary' | 'destructive' | 'outline' {
	if (status === 'CONFIRMED') return 'default';
	if (status === 'DRAFT') return 'secondary';
	return 'outline';
}

/** 일괄 생성/확정 결과의 skip 사유 라벨 */
export function skipReasonLabel(reason: string): string {
	const map: Record<string, string> = {
		EMPLOYEE_NOT_ACTIVE: '재직 상태가 아님',
		ALREADY_EXISTS: '이미 발행됨',
		EMPTY_TEMPLATE: '템플릿 항목 없음',
		PAYROLL_SETUP_MISSING: '연봉계약/추가항목 미설정',
		EMPLOYMENT_CONTRACT_REQUIRED: '서명 완료 또는 승인된 근로계약서 필요',
		PAYROLL_EXCLUDED: '급여 정산 제외',
		OUT_OF_PERIOD: '급여 산정 기간 밖',
		ERROR: '처리 오류'
	};
	return map[reason] ?? reason;
}

export interface EarningRow {
	code: string;
	name: string;
	taxable: boolean;
	amount: number;
	description: string;
}

/**
 * 지급항목 프리셋. MEAL_ALLOWANCE+비과세 조합은 서버에서 비과세 식대 한도가 적용된다 —
 * 식대 프리셋의 code를 바꾸면 한도 처리가 빠지므로 주의.
 */
export const EARNING_PRESETS: { label: string; row: EarningRow }[] = [
	{
		label: '기본급',
		row: { code: 'BASIC_PAY', name: '기본급', taxable: true, amount: 0, description: '' }
	},
	{
		label: '식대(비과세)',
		row: { code: 'MEAL_ALLOWANCE', name: '식대', taxable: false, amount: 0, description: '' }
	},
	{
		label: '상여금',
		row: { code: 'BONUS', name: '상여금', taxable: true, amount: 0, description: '' }
	},
	{
		label: '직접 입력',
		row: { code: 'CUSTOM', name: '', taxable: true, amount: 0, description: '' }
	}
];

/** 공제항목 프리셋. 과거 명세서 수동 등록에서 사용 — 코드는 백엔드 표준 공제 코드와 일치해야 한다. */
export const DEDUCTION_PRESETS: { label: string; row: EarningRow }[] = [
	{
		label: '국민연금',
		row: { code: 'NATIONAL_PENSION', name: '국민연금', taxable: false, amount: 0, description: '' }
	},
	{
		label: '건강보험',
		row: { code: 'HEALTH_INSURANCE', name: '건강보험', taxable: false, amount: 0, description: '' }
	},
	{
		label: '장기요양',
		row: { code: 'LONGTERM_CARE', name: '장기요양보험', taxable: false, amount: 0, description: '' }
	},
	{
		label: '고용보험',
		row: {
			code: 'EMPLOYMENT_INSURANCE',
			name: '고용보험',
			taxable: false,
			amount: 0,
			description: ''
		}
	},
	{
		label: '소득세',
		row: { code: 'INCOME_TAX', name: '소득세', taxable: false, amount: 0, description: '' }
	},
	{
		label: '지방소득세',
		row: { code: 'LOCAL_INCOME_TAX', name: '지방소득세', taxable: false, amount: 0, description: '' }
	},
	{
		label: '직접 입력',
		row: { code: 'CUSTOM_DEDUCTION', name: '', taxable: false, amount: 0, description: '' }
	}
];

export function importStatusLabel(status: PayrollImportStatus | string): string {
	const map: Record<string, string> = {
		UPLOADED: '업로드됨',
		EXTRACTING: '추출 중',
		EXTRACTED: '추출 완료',
		FAILED: '실패'
	};
	return map[status] ?? status;
}

export function importStatusVariant(
	status: PayrollImportStatus | string
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'EXTRACTED') return 'success';
	if (status === 'EXTRACTING') return 'warning';
	if (status === 'FAILED') return 'destructive';
	return 'outline';
}

export function importRowStatusLabel(status: PayrollImportRowStatus | string): string {
	const map: Record<string, string> = {
		PENDING: '검토 대기',
		COMMITTED: '등록됨',
		SKIPPED: '건너뜀',
		FAILED: '실패'
	};
	return map[status] ?? status;
}

export function importRowStatusVariant(
	status: PayrollImportRowStatus | string
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'COMMITTED') return 'success';
	if (status === 'PENDING') return 'warning';
	if (status === 'FAILED') return 'destructive';
	return 'outline';
}

/** commit 결과 행의 실패/건너뜀 사유 라벨 */
export function importRowFailReasonLabel(reason: string | null | undefined): string {
	if (!reason) return '-';
	const map: Record<string, string> = {
		EMPLOYEE_NOT_MATCHED: '직원 미매칭',
		MISSING_PERIOD: '귀속연월 누락',
		EMPTY_EARNINGS: '지급 항목 없음',
		ALREADY_EXISTS: '같은 월 명세서 존재',
		ERROR: '처리 오류'
	};
	return map[reason] ?? reason;
}

export function monthLabel(year: number, month: number): string {
	return `${year}년 ${month}월`;
}
